import { Id } from "@cvx/_generated/dataModel";
import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * A user can be either a creator or an app founder.
 * An app founder can own and manage multiple apps.
 * The same user cannot be both a creator and an app founder.
 */

interface AccountState {
  contractId?: Id<"creatorContracts">;
  creatorId?: Id<"creators">;
  appId?: Id<"apps">;

  setCreatorId: (id: Id<"creators"> | undefined) => void;
  setAppId: (id: Id<"apps"> | undefined) => void;
  setContractId: (id: Id<"creatorContracts"> | undefined) => void;

  pendingCampaignId?: Id<"campaigns">; // Temporary storage for campaign ID during auth flow
  setPendingCampaignId: (id: Id<"campaigns"> | undefined) => void;
  clearPendingCampaignId: () => void;

  // Frontend-only demo mode flag
  demoModeEnabled: boolean;
  setDemoModeEnabled: (enabled: boolean) => void;

  // Custom demo app settings
  demoAppName: string;
  demoAppLogo: string;
  setDemoAppName: (name: string) => void;
  setDemoAppLogo: (logo: string) => void;

  clearAccount: () => void;
}

export const useAccount = create<AccountState>()(
  persist(
    (set) => ({
      creatorId: undefined,
      appId: undefined,
      contractId: undefined,
      pendingCampaignId: undefined,
      demoModeEnabled: false,
      demoAppName: "TechFlow Pro",
      demoAppLogo:
        "https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=200&h=200&fit=crop&crop=center",
      setCreatorId: (id) => set({ creatorId: id, appId: undefined }),
      setAppId: (id) =>
        set({ appId: id, creatorId: undefined, contractId: undefined }),
      setContractId: (id) => set({ contractId: id, appId: undefined }),
      setPendingCampaignId: (id) => set({ pendingCampaignId: id }),
      clearPendingCampaignId: () => set({ pendingCampaignId: undefined }),
      setDemoModeEnabled: (enabled) => set({ demoModeEnabled: enabled }),
      setDemoAppName: (name) => set({ demoAppName: name }),
      setDemoAppLogo: (logo) => set({ demoAppLogo: logo }),
      clearAccount: () =>
        set({
          creatorId: undefined,
          appId: undefined,
          contractId: undefined,
          pendingCampaignId: undefined,
          demoModeEnabled: false,
          demoAppName: "TechFlow Pro",
          demoAppLogo:
            "https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=200&h=200&fit=crop&crop=center",
        }),
    }),
    {
      name: "account-storage",
      partialize: (state) => ({
        contractId: state.contractId,
        creatorId: state.creatorId,
        appId: state.appId,
        pendingCampaignId: state.pendingCampaignId,
        demoModeEnabled: state.demoModeEnabled,
        demoAppName: state.demoAppName,
        demoAppLogo: state.demoAppLogo,
      }),
    },
  ),
);
